/**
 * Admin (staff / agent) logout helpers.
 *
 * Admin counterpart to `lib/auth/customerLogout.ts`. Centralises the
 * "which login URL should we send this staff user back to?" decision so every
 * admin-facing sign-out surface (the admin layout menu, the agent inactivity
 * guard) returns the user to their *branded* `/admin/login/{root-parent-slug}`
 * page rather than the slug-less global (Penny / solutions) admin login.
 *
 * Branded admin login is single-tenanted at the **root parent** level — child
 * org staff walk up to their parent's slug (mirrors `resolveRootOrgSlug`).
 * Global admins, who hold a NULL-org `global-admin` assignment only, keep the
 * slug-less `/admin/login`.
 *
 * The assignment + org-hierarchy lookup is RLS-bound, so it MUST run BEFORE
 * `supabase.auth.signOut()` — afterwards the authenticated SELECT returns zero
 * rows and we fall back to the slug-less route. This is the same ordering
 * contract `customerLogout.ts` documents for `customer_referrals`.
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import { safeAssignLocation } from '@/lib/security/safeRedirect';
import { resolveRootOrgSlug } from '@/lib/auth/login-url';
import {
  clearBrandedLoginSlugFromDocument,
  readBrandedLoginSlugFromDocument,
} from '@/lib/auth/brandedLoginCookie';
import {
  appUrlIsRecognisedApex,
  buildTenantUrl,
  getAppOrigin,
  getTenantRedirectAllowedHosts,
} from '@/lib/host/tenantUrl';

// ---------------------------------------------------------------------------
// Sign-out redirect sentinel (#617 race, mobile-WebKit edition)
// ---------------------------------------------------------------------------
// `signOutAdminAndRedirect` resolves the branded login URL and hard-navigates
// via `window.location.href`. But `await signOut()` flips AuthContext `user`
// to `null` first, so `ProtectedRoute`'s unauthenticated-redirect effect fires
// `router.push('/admin/login')` (slug-less) to evict the visitor. On desktop
// the synchronous hard navigation wins that race; on slower navigation commits
// (mobile WebKit / Safari) Next's RSC-fetch-failure fallback then re-runs
// `window.location.href = '/admin/login'`, clobbering the branded URL.
//
// This module-scoped flag lets the auth gate skip its slug-less redirect while
// a branded sign-out is mid-flight. On the happy path the hard navigation tears
// down the JS context, so the flag dies with the page and never leaks across
// navigations; `signOutAdminAndRedirect` clears it explicitly if no navigation
// was issued (SSR, or `signOut` threw) so the guard is never left wedged.
let adminSignOutRedirectInFlight = false;

/** Mark a branded admin sign-out as in progress (suppresses the auth-gate redirect). */
export function beginAdminSignOutRedirect(): void {
  adminSignOutRedirectInFlight = true;
}

/** Clear the sign-out-in-progress flag. */
export function endAdminSignOutRedirect(): void {
  adminSignOutRedirectInFlight = false;
}

/** True while a branded admin sign-out is performing its own hard navigation. */
export function isAdminSignOutRedirectInFlight(): boolean {
  return adminSignOutRedirectInFlight;
}

/**
 * Resolve the staff user's "home" root-parent organisation slug.
 *
 * Global admins are resolved FIRST via the canonical
 * `user_has_permission_anywhere(uid, 'users.manage_global_admins')` RPC and
 * always return `null` (slug-less login) — even when they *also* hold an
 * org-scoped assignment (e.g. the default `admin@penny.local`, who owns the
 * `penny` org). Without this precedence check a global admin who happens to
 * own an org would be bounced to that org's branded login — the #617
 * regression in reverse.
 *
 * For everyone else: picks the user's first *org-scoped* role assignment
 * (NULL-org assignments are excluded), then walks the `parent_org_id` chain to
 * the topmost org. Returns `null` when no org-scoped assignment is visible
 * (no assignment, RLS-hidden rows, or query error). Non-throwing; fails closed
 * (any RPC error is treated as "not a global admin" and falls through to the
 * org lookup).
 *
 * IMPORTANT: call this BEFORE `signOut()`. Once the session ends the
 * `user_role_assignments` and `organizations` SELECTs are evaluated with
 * `auth.uid() = null` and return no rows.
 */
export async function getAdminHomeOrgSlug(
  supabase: SupabaseClient,
  userId: string,
): Promise<string | null> {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data: isGlobalAdmin } = await (supabase as any).rpc(
      'user_has_permission_anywhere',
      { user_uuid: userId, perm_slug: 'users.manage_global_admins' },
    );
    if (isGlobalAdmin === true) return null;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data } = await (supabase as any)
      .from('user_role_assignments')
      .select('organization_id')
      .eq('user_id', userId)
      .not('organization_id', 'is', null)
      .limit(1)
      .maybeSingle();
    const organizationId: string | undefined = data?.organization_id ?? undefined;
    if (!organizationId) return null;
    return await resolveRootOrgSlug(supabase, organizationId);
  } catch {
    return null;
  }
}

/**
 * Build the admin-facing login URL.
 *
 * Returns a same-origin path (`/admin/login[/slug]`) by default. When
 * subdomain routing is enabled and a slug is provided, returns an absolute
 * URL on the tenant subdomain (`https://<slug>.<apex>/admin/login`) so the
 * hard-navigation in {@link signOutAdminAndRedirect} lands on the correct
 * origin. Both `window.location.href` and Next.js `redirect()` accept
 * absolute URLs, so callers are unchanged.
 *
 * @param slug   root-parent org slug from `getAdminHomeOrgSlug`, or `null`
 * @param query  optional query string (without leading `?`), e.g. `'reason=inactivity'`
 */
export function adminLoginPath(slug: string | null, query?: string): string {
  if (slug) {
    const origin = getAppOrigin();
    if (origin && appUrlIsRecognisedApex(origin)) {
      return buildTenantUrl({
        appUrl: origin,
        slug,
        path: '/admin/login',
        query,
      });
    }
  }
  const base = slug ? `/admin/login/${slug}` : '/admin/login';
  return query ? `${base}?${query}` : base;
}

/**
 * One-shot helper: capture the slug, sign the user out, return the branded
 * login path. Convenience wrapper for the common admin sign-out pattern.
 *
 * Callers are responsible for navigating to the returned path themselves; the
 * right navigation primitive depends on whether the caller is a client
 * component, route handler, or server action.
 */
export async function signOutAdminAndGetLoginPath(opts: {
  supabase: SupabaseClient;
  /**
   * The actual sign-out function. Pass `useAuth().signOut` from a client
   * component, or a thin wrapper around `supabase.auth.signOut()` elsewhere.
   */
  signOut: () => Promise<unknown>;
  query?: string;
}): Promise<string> {
  let slug: string | null = null;
  try {
    // Prefer the `last_admin_login_slug` cookie, set by the SSO callback and
    // by the branded `/admin/login/{slug}` page on successful sign-in. This
    // is the source of truth for "where did the user actually authenticate?"
    // and supersedes the role-assignment walk in three reported scenarios
    // (#706 follow-up):
    //
    //   1. global admin who entered via a tenant SSO,
    //   2. multi-tenant user signing into a tenant other than their primary,
    //   3. any branded password sign-in by a multi-tenant / global user.
    //
    // The cookie is SSR-safe (returns `null` server-side); a missing or
    // tamper-rejected value falls through to `getAdminHomeOrgSlug`, which
    // preserves the existing #617 contract for slug-less entrants.
    slug = readBrandedLoginSlugFromDocument();
    if (!slug) {
      const { data: { user } } = await opts.supabase.auth.getUser();
      if (user) slug = await getAdminHomeOrgSlug(opts.supabase, user.id);
    }
  } catch {
    // Non-fatal — fall through to the slug-less path.
  }
  await opts.signOut();
  // Clear the branded-login cookie now that we have already captured the slug
  // and the session has ended. Lifecycle becomes "set on login → cleared on
  // logout" rather than relying on a fixed Max-Age, which closes the edge
  // case where a user switches login methods (e.g. signs in via slug-less
  // social OAuth after a previous branded sign-in) and would otherwise still
  // carry the stale slug. SSR-safe: the helper no-ops without `document`.
  clearBrandedLoginSlugFromDocument();
  return adminLoginPath(slug, opts.query);
}

/**
 * Client-side variant: sign the staff user out and perform a hard navigation
 * to the resolved branded login URL.
 *
 * Always prefer this over `signOutAdminAndGetLoginPath` + `router.push()`
 * inside admin-protected pages. Soft navigation races against
 * `ProtectedRoute` (and the admin layout auth gate), which fire the moment
 * AuthContext flips `user` to `null` and call `router.push('/admin/login')`
 * (slug-less), clobbering the branded push — the exact regression behind
 * issue #617.
 *
 * `window.location.href` tears down the React tree synchronously, so no hook
 * effect gets a chance to override the destination.
 */
export async function signOutAdminAndRedirect(opts: {
  supabase: SupabaseClient;
  signOut: () => Promise<unknown>;
  query?: string;
}): Promise<void> {
  // Raise the sentinel BEFORE `signOut` flips `user` to null, so
  // `ProtectedRoute`'s redirect effect bows out instead of racing this hard
  // navigation (see the sentinel block above and #617).
  beginAdminSignOutRedirect();
  let navigated = false;
  try {
    const target = await signOutAdminAndGetLoginPath(opts);
    if (typeof window !== 'undefined') {
      navigated = true;
      // `adminLoginPath` returns a same-origin path (`/admin/login[/slug]`)
      // when subdomain routing is off, and an absolute tenant-subdomain URL
      // when it is on. Pass it through the safe-redirect validator with the
      // configured tenant allow-list for defence-in-depth. On rejection,
      // fall back to the slug-less same-origin login route.
      safeAssignLocation(
        target,
        {
          allowedHosts: getTenantRedirectAllowedHosts(),
          allowInsecure: true,
        },
        () => {
          window.location.href = adminLoginPath(null, opts.query);
        },
      );
    }
  } finally {
    // If no navigation was issued (SSR, or `signOut` threw before the hard
    // nav), drop the sentinel so the auth gate can still evict the now
    // unauthenticated user. On the happy path the hard navigation tears down
    // this JS context, so the flag dies with the page.
    if (!navigated) endAdminSignOutRedirect();
  }
}
